import { Button, Input, Switcher } from '@bennows/boows-react';
import { Check, X } from 'lucide-react';
import { type FormEvent, type ReactNode, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useMe } from '../auth/useMe';
import { readableSearchTypes, SearchResultGroups } from './SearchResultGroups';
import { SEARCH_MAX_LENGTH, SEARCH_TYPES, SearchLoadError, type SearchEntityType } from './search-api';
import { isSearchable, useSearchQuery } from './useSearch';

export interface SearchFilters {
  readonly types: readonly SearchEntityType[];
  readonly archived: boolean;
}

export interface SearchPanelProps {
  /** `id` of the field (the `/` shortcut focuses it when it is on screen). */
  readonly inputId: string;
  readonly inputRef: RefObject<HTMLInputElement>;
  /** What the field shows. */
  readonly draft: string;
  readonly onDraftChange: (draft: string) => void;
  /** What is searched: the draft after a pause in typing, or at once on Enter. */
  readonly query: string;
  readonly onSubmit: () => void;
  readonly filters: SearchFilters;
  readonly onFiltersChange: (change: Partial<SearchFilters>) => void;
}

/**
 * The search itself (spec 110 §6, S110-8): one field, type chips, the archived switch and the
 * results grouped by type in the server's relevance order. Rendered by the search page (state in
 * the URL) and by the search overlay of the app frame (state local, S150-8); the server filters
 * every type by the member's permissions, the chips only hide what the member could never see.
 */
export function SearchPanel({ inputId, inputRef, draft, onDraftChange, query, onSubmit, filters, onFiltersChange }: SearchPanelProps) {
  const { t } = useTranslation();
  const me = useMe();
  const { types, archived } = filters;
  const search = useSearchQuery({ q: query, types: [...types], includeArchived: archived });
  const searchable = isSearchable(query);
  const results = search.data?.pages.flatMap((page) => page.data) ?? [];
  const available = readableSearchTypes(me.data?.active_tenant?.permissions ?? []);
  const filtered = types.length > 0 || archived;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit();
  };

  const toggleType = (type: SearchEntityType) =>
    onFiltersChange({ types: types.includes(type) ? types.filter((current) => current !== type) : [...types, type] });

  const clearField = () => {
    onDraftChange('');
    inputRef.current?.focus();
  };

  const body = (): ReactNode => {
    if (!searchable) return <p className="ui-field__hint">{t('search.hint.tooShort')}</p>;
    if (search.isError && !search.isFetchNextPageError) {
      const error = search.error;
      const tooMany = error instanceof SearchLoadError && error.reason === 'too_many_terms';
      return (
        <div className="ui-error-state" role="alert">
          <p className="ui-error-state__text">{t(tooMany ? 'search.error.tooManyTerms' : 'search.error.failed')}</p>
          {tooMany ? null : (
            <Button variant="secondary" onClick={() => void search.refetch()}>
              {t('error.retry')}
            </Button>
          )}
        </div>
      );
    }
    if (search.isPending) {
      return (
        <div aria-hidden="true">
          <span className="ui-skeleton ui-skeleton--text" />
          <span className="ui-skeleton ui-skeleton--text" />
          <span className="ui-skeleton ui-skeleton--text" />
        </div>
      );
    }
    if (results.length === 0) {
      return (
        <div className="ui-empty-state">
          <p>{t('search.empty.title', { query: query.trim() })}</p>
          <p className="ui-empty-state__text">{t('search.empty.text')}</p>
          {filtered ? (
            <Button variant="secondary" onClick={() => onFiltersChange({ types: [], archived: false })}>
              {t('search.filters.reset')}
            </Button>
          ) : null}
        </div>
      );
    }
    return (
      <>
        <SearchResultGroups results={results} />
        {search.isFetchNextPageError ? (
          <div className="ui-error-state" role="alert">
            <p className="ui-error-state__text">{t('search.results.loadMoreFailed')}</p>
            <Button variant="secondary" onClick={() => void search.fetchNextPage()}>
              {t('error.retry')}
            </Button>
          </div>
        ) : search.hasNextPage ? (
          <div className="search-more">
            <Button variant="secondary" loading={search.isFetchingNextPage} onClick={() => void search.fetchNextPage()}>
              {t(search.isFetchingNextPage ? 'search.results.loadingMore' : 'search.results.loadMore')}
            </Button>
          </div>
        ) : null}
      </>
    );
  };

  // Announced once per settled search, never per keystroke.
  const status = !searchable ? '' : search.isPending ? t('search.status.loading') : search.isSuccess ? t('search.status.results', { count: results.length }) : '';

  return (
    <>
      <form className="ui-stack" role="search" aria-label={t('search.page.title')} onSubmit={submit}>
        <Input
          ref={inputRef}
          id={inputId}
          type="search"
          label={t('search.field.label')}
          hideLabel
          placeholder={t('search.field.placeholder')}
          value={draft}
          onChange={onDraftChange}
          maxLength={SEARCH_MAX_LENGTH}
          autoComplete="off"
          enterKeyHint="search"
          suffix={
            draft ? (
              <Button variant="ghost" size="sm" iconOnly aria-label={t('search.field.clear')} onClick={clearField} type="button">
                <X aria-hidden="true" />
              </Button>
            ) : undefined
          }
        />
        <div className="ui-stack" role="group" aria-label={t('search.filters.label')}>
          <div className="ui-filter-chips search-chips" role="group" aria-label={t('search.filters.types')}>
            {SEARCH_TYPES.filter((type) => available.includes(type)).map((type) => {
              const active = types.includes(type);
              return (
                <Button
                  key={type}
                  type="button"
                  variant={active ? 'primary' : 'secondary'}
                  aria-pressed={active}
                  iconPrefix={active ? <Check aria-hidden="true" size={14} /> : undefined}
                  onClick={() => toggleType(type)}
                >
                  {t(`search.types.${type}`)}
                </Button>
              );
            })}
          </div>
          <Switcher label={t('search.filters.archived')} checked={archived} onChange={(next) => onFiltersChange({ archived: next })} />
        </div>
      </form>
      <p className="ui-visually-hidden" role="status">
        {status}
      </p>
      {body()}
    </>
  );
}
